/**
 * ShellLayout — admin.mypcb.com, the one admin portal for every platform
 * (docs/architecture/admin-portal-shape.md, approved 2026-10-16; redesigned 0.53.0, Bill 2026-11-03).
 *
 * The frame is the shared @retail-ops/panel-shell (also used by risk.mypcb.com and sysops.mypcb.com):
 * a sidebar with the platform switcher (only the platforms where the signed-in person holds some admin
 * right, as each platform's own API reports it, plus Shared for Retail admins), page search (Ctrl K),
 * and the current platform's six sections with their pages; a top bar with breadcrumbs, the platform
 * API's version, the guided tour, per-page help and the account menu (About, Sign out). Under 900px
 * the sidebar is a drawer. Menus come from platforms/adminNav.ts, gated exactly as before.
 */

import { Link, Outlet, useLocation, useNavigate } from 'react-router';
import { PanelShell, type ShellLinkComponent } from '@retail-ops/panel-shell';
import { TourStartButton } from '@retail-ops/guided-tour';
import { PanelSupportReport } from './PanelSupportReport';
import { PanelGuidedTours } from '@/tours/PanelGuidedTours';
import { useAuth } from '@/contexts/AuthContext';
import { useAdminShell } from '@/platforms/adminNav';
import { APP_VERSION } from '@/constants/version';

export function ShellLayout(): JSX.Element {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const model = useAdminShell(pathname);

  return (
    <PanelGuidedTours>
      <PanelShell
        appName="Admin"
        version={APP_VERSION}
        pathname={pathname}
        Link={Link as unknown as ShellLinkComponent}
        navigate={(to) => void navigate(to)}
        nav={model.nav}
        platforms={model.platforms}
        currentPlatformId={model.currentId}
        searchItems={model.searchItems}
        apiVersion={model.apiVersion}
        user={user ? { displayName: user.displayName || user.upn, detail: user.upn } : null}
        onSignOut={() => void logout()}
        aboutPath="/about"
        crumbs={pathname === '/about' ? [{ label: 'About this app' }] : undefined}
        topbarExtras={<TourStartButton className="btn btn--ghost btn--sm btn--invert" />}
        changesNeedReason
        homeTour="panel-home"
        userTour="panel-user"
      >
        <Outlet />
      </PanelShell>
      <PanelSupportReport />
    </PanelGuidedTours>
  );
}
